import type { ReactNode } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import type { LucideIcon } from 'lucide-react'

interface SimpleListPageProps<T extends object> {
  title: string
  description?: string
  icon: LucideIcon
  rows: T[] | undefined
  isLoading: boolean
  error?: unknown
  columns: ColumnDef<T, unknown>[]
  emptyTitle: string
  emptyDescription?: string
  actions?: ReactNode
  emptyAction?: ReactNode
  beforeTable?: ReactNode
  afterTable?: ReactNode
}

export function SimpleListPage<T extends object>({
  title,
  description,
  icon,
  rows,
  isLoading,
  error,
  columns,
  emptyTitle,
  emptyDescription,
  actions,
  emptyAction,
  beforeTable,
  afterTable,
}: SimpleListPageProps<T>) {
  return (
    <Page title={title} description={description} actions={actions}>
      {beforeTable}
      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load data.'}
        </div>
      ) : (
        <DataTable
          columns={columns as never}
          rows={rows ?? []}
          loading={isLoading}
          empty={
            <EmptyState
              icon={icon}
              title={emptyTitle}
              description={emptyDescription}
              action={emptyAction}
            />
          }
        />
      )}
      {afterTable}
    </Page>
  )
}
